import React, { useEffect, useRef } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
} from 'react-native';
import PrimaryButton from './PrimaryButton';
import TertiaryButton from './TertiaryButton';

interface BaseModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  message: string;
  icon?: React.ReactNode;
  primaryButtonText?: string;
  onPrimaryPress?: () => void;
  primaryButtonDisabled?: boolean;
  secondaryButtonText?: string;
  onSecondaryPress?: () => void;
  secondaryButtonDisabled?: boolean;
}

export default function BaseModal({
  visible,
  onClose,
  title,
  message,
  icon,
  primaryButtonText,
  onPrimaryPress,
  primaryButtonDisabled = false,
  secondaryButtonText,
  onSecondaryPress,
  secondaryButtonDisabled = false,
}: BaseModalProps) {
  const slideAnim = useRef(new Animated.Value(300)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      // Reset animations to starting position
      slideAnim.setValue(300);
      fadeAnim.setValue(0);
      
      // Animate in
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(slideAnim, {
          toValue: 0,
          damping: 20,
          stiffness: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handleClose = () => {
    // Animate out first
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 300,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      // Call onClose after animation completes
      onClose();
    });
  };

  const handlePrimaryPress = () => {
    if (onPrimaryPress) {
      // Animate out first
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 300,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start(() => {
        // Call the action after animation completes
        onPrimaryPress();
      });
    } else {
      handleClose();
    }
  };

  const handleSecondaryPress = () => {
    if (onSecondaryPress) {
      // Animate out first
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 300,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start(() => {
        // Call the action after animation completes
        onSecondaryPress();
      });
    } else {
      handleClose();
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <TouchableOpacity 
        style={styles.overlay}
        activeOpacity={1}
        onPress={handleClose}
      >
        <Animated.View 
          style={[
            styles.overlayBackground,
            { opacity: fadeAnim }
          ]}
        />
        <View style={styles.modalContainer}>
          <TouchableOpacity 
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
          >
            <Animated.View
              style={[
                styles.modalContent,
                {
                  transform: [{ translateY: slideAnim }],
                  opacity: fadeAnim,
                }
              ]}
            >
            {/* Header Section */}
            <View style={styles.headerSection}>
              {icon && (
                <View style={styles.iconContainer}>
                  {icon}
                </View>
              )}
              <Text style={styles.title}>{title}</Text>
            </View>
            
            {/* Body Section */}
            <View style={styles.bodySection}>
              <Text style={styles.message}>{message}</Text>
            </View>
            
            {/* Button Section */}
            <View style={styles.buttonSection}>
              {primaryButtonText && (
                <PrimaryButton
                  title={primaryButtonText}
                  onPress={handlePrimaryPress}
                  disabled={primaryButtonDisabled}
                />
              )}
              {secondaryButtonText && (
                <TertiaryButton
                  title={secondaryButtonText}
                  onPress={handleSecondaryPress}
                  disabled={secondaryButtonDisabled}
                />
              )}
            </View>
          </Animated.View>
        </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlayBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContainer: {
    paddingHorizontal: 24,
    width: '100%',
    maxWidth: 448,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
  },
  headerSection: {
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 0,
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    color: '#151515',
    lineHeight: 28.8,
  },
  bodySection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 0,
  },
  message: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
  },
  buttonSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 12,
  },
});
